/**
 * Core Chat Hook
 *
 * Wraps @ai-sdk/react useChat() and standardizes the boilerplate that
 * every chat hook reimplements: input state, send/stop/clear handlers,
 * isSending derivation, and error handling.
 *
 * Feature hooks compose this with persistence, context slugs, or
 * SSE streaming as needed.
 *
 * @module hooks/chat/useChatCore
 */

import { useState, useCallback, useRef } from 'react';
import { useChat, type UIMessage } from '@ai-sdk/react';
import type { ChatAddToolOutputFunction } from 'ai';
import type { ChatCoreOptions, ChatCoreReturn } from './types';

export function useChatCore<TMessage extends UIMessage = UIMessage>(
  options: ChatCoreOptions<TMessage>
): ChatCoreReturn<TMessage> {
  const {
    chatId,
    transport,
    throttle = 40,
    onFinish,
    onError,
    canSend,
    beforeSend,
    afterSend,
    onToolCall,
    sendAutomaticallyWhen,
    externalBusy,
  } = options;

  const [input, setInput] = useState('');

  // `onToolCall` is a useChat() CONSTRUCTOR option, but the `addToolOutput` it
  // needs to call back with only exists on that same call's RETURN value.
  // Resolved once, here, with a ref — so feature hooks never have to.
  const addToolOutputRef = useRef<ChatAddToolOutputFunction<TMessage> | null>(null);

  const chat = useChat<TMessage>({
    id: chatId,
    transport: transport ?? undefined,
    experimental_throttle: throttle,
    onFinish: onFinish
      ? ({ message, finishReason }) => onFinish({ message, finishReason })
      : undefined,
    onError: (error) => {
      console.error(`[Chat:${chatId}] Error:`, error);
      onError?.(error);
    },
    onToolCall: onToolCall
      ? ({ toolCall }) => {
          const addToolOutput = addToolOutputRef.current;
          if (!addToolOutput) {
            // Safe today — the ref is assigned in the render body and a tool
            // call can only arrive from a stream that starts later. Asserted
            // rather than `!` so a reordering shows up as this message instead
            // of an anonymous TypeError.
            throw new Error('useChatCore: onToolCall fired before addToolOutput was available');
          }
          return onToolCall({ toolCall, addToolOutput });
        }
      : undefined,
    sendAutomaticallyWhen,
  });

  const { messages, status, sendMessage: sdkSendMessage, stop, setMessages, addToolOutput } = chat;
  addToolOutputRef.current = addToolOutput;

  const isSending = status === 'streaming' || status === 'submitted' || !!externalBusy;

  const sendText = useCallback(
    async (value: string, metadata?: Record<string, unknown>) => {
      if (!value) return;
      if (canSend && !canSend()) return;

      try {
        await beforeSend?.(value, metadata);
        // `metadata` here is caller-supplied (e.g. an onboarding kickoff's
        // marker kind), not narrowed to this chat's `InferUIMessageMetadata`
        // — the SDK's own message metadata type is effectively `unknown` at
        // this generic boundary, so there's nothing narrower to assert to.
        await sdkSendMessage({ text: value, metadata } as Parameters<typeof sdkSendMessage>[0]);
        afterSend?.();
      } catch (error) {
        console.error(`[Chat:${chatId}] Failed to send:`, error);
        onError?.(error instanceof Error ? error : new Error('Failed to send message'));
        throw error;
      }
    },
    [sdkSendMessage, canSend, beforeSend, afterSend, onError, chatId]
  );

  const sendMessage = useCallback(async () => {
    const value = input.trim();
    if (!value) return;
    // Gate BEFORE clearing: a blocked send (`canSend` false) returns quietly
    // from `sendText`, and the composer must still hold what was typed.
    if (canSend && !canSend()) return;
    try {
      setInput('');
      await sendText(value);
    } catch {
      // sendText already reported the error; restore the composer text.
      setInput(value);
    }
  }, [input, canSend, sendText]);

  const handleStop = useCallback(() => {
    void stop();
  }, [stop]);

  const clear = useCallback(() => {
    setMessages([]);
    setInput('');
  }, [setMessages]);

  return {
    messages,
    input,
    setInput,
    status,
    isSending,
    sendMessage,
    sendText,
    stop: handleStop,
    clear,
    setMessages,
    addToolOutput,
  };
}
